import {AlertIcon} from '@primer/octicons-react'
import {Blankslate} from '@primer/react/experimental'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import {ErrorPage404Component} from './components/ErrorPage404Component'
import {ErrorPage500Component} from './components/ErrorPage500Component'
import {
  ErrorPageTemplateComponent,
  type ErrorPageTemplateStatusCode,
  type ErrorPageTemplateType,
} from './components/ErrorPageTemplateComponent'

export type ErrorPageType = ErrorPageTemplateType | '404' | '500'
type ErrorPageTemplateNumericStatusCode = 403 | 410 | 420 | 429 | 502 | 503
type ErrorPageNonTemplateStatusCode = '404' | '500'
type ErrorPageNonTemplateNumericStatusCode = 404 | 500

type ErrorPageTemplateProps = {
  isEnterprise?: boolean
  returnTo?: string
}

type ErrorPageNonTemplateProps = {
  isEnterprise?: never
  returnTo?: never
}

export type ErrorPageEmbeddedProps = {
  isEmbedded?: boolean
}

export type RepositoryFileNotFoundErrorPageProps = {
  repositoryName: string
  repositoryOverviewUrl: string
}

export type RepositoryErrorPageProps = ErrorPageEmbeddedProps & {
  action?: {
    label: string
    url: string
  }
  className?: string
  description: ReactNode
  heading?: ReactNode
  visual?: ReactNode
}

const defaultRepositoryErrorVisual = <AlertIcon size="medium" />

export type ErrorPagesProps =
  | (ErrorPageEmbeddedProps & ErrorPageTemplateProps & {type: ErrorPageTemplateType; errorCode?: never})
  | (ErrorPageEmbeddedProps &
      ErrorPageTemplateProps & {
        errorCode: ErrorPageTemplateStatusCode | ErrorPageTemplateNumericStatusCode
        type?: never
      })
  | (ErrorPageEmbeddedProps & ErrorPageNonTemplateProps & {type?: ErrorPageNonTemplateStatusCode; errorCode?: never})
  | (ErrorPageEmbeddedProps &
      ErrorPageNonTemplateProps & {
        errorCode: ErrorPageNonTemplateStatusCode | ErrorPageNonTemplateNumericStatusCode
        type?: never
      })

function normalizeErrorPageType(
  type: ErrorPageType | ErrorPageTemplateNumericStatusCode | ErrorPageNonTemplateNumericStatusCode | undefined,
): ErrorPageType {
  switch (type) {
    case '403':
    case 403:
      return '403'
    case '404':
    case 404:
      return '404'
    case '410':
    case 410:
      return '410'
    case '420':
    case 420:
      return '420'
    case '429':
    case 429:
      return '429'
    case '502':
    case 502:
      return '502'
    case '503':
    case 503:
      return '503'
    case 'maintenance':
      return 'maintenance'
    case 'offline':
      return 'offline'
    case 'switching_runtime':
      return 'switching_runtime'
    default:
      return '500'
  }
}

export function ErrorPages({
  type = '500',
  errorCode,
  isEmbedded = false,
  isEnterprise = false,
  returnTo,
}: ErrorPagesProps) {
  const errorPageType = normalizeErrorPageType(errorCode ?? type)

  if (errorPageType === '404') {
    return <ErrorPage404Component isEmbedded={isEmbedded} />
  }

  if (errorPageType === '500') {
    return <ErrorPage500Component isEmbedded={isEmbedded} />
  }

  return (
    <ErrorPageTemplateComponent
      type={errorPageType}
      isEmbedded={isEmbedded}
      isEnterprise={isEnterprise}
      returnTo={returnTo}
    />
  )
}

export function NotFoundErrorPage({isEmbedded = false}: ErrorPageEmbeddedProps = {}) {
  return <ErrorPages type="404" isEmbedded={isEmbedded} />
}

export function ForbiddenErrorPage({
  isEmbedded = false,
  returnTo,
}: ErrorPageEmbeddedProps & Pick<ErrorPageTemplateProps, 'returnTo'> = {}) {
  return <ErrorPages type="403" isEmbedded={isEmbedded} returnTo={returnTo} />
}

export function GoneErrorPage() {
  return <ErrorPages type="410" />
}

export function RateLimitedErrorPage({
  type = '429',
  isEmbedded = false,
  isEnterprise = false,
  returnTo,
}: ErrorPageEmbeddedProps &
  Pick<ErrorPageTemplateProps, 'isEnterprise' | 'returnTo'> & {
    type?: '420' | '429'
  } = {}) {
  return <ErrorPages type={type} isEmbedded={isEmbedded} isEnterprise={isEnterprise} returnTo={returnTo} />
}

export function InternalServerErrorPage({isEmbedded = false}: ErrorPageEmbeddedProps = {}) {
  return <ErrorPage500Component isEmbedded={isEmbedded} />
}

export function RepositoryFileNotFoundErrorPage({
  repositoryName,
  repositoryOverviewUrl,
}: RepositoryFileNotFoundErrorPageProps) {
  return (
    <RepositoryErrorPage
      action={{label: 'Return to the repository overview', url: repositoryOverviewUrl}}
      description={
        <>
          The requested file could not be found in <strong>{repositoryName}</strong>.
        </>
      }
      isEmbedded
    />
  )
}

export function RepositoryErrorPage({
  description,
  action,
  heading = '404 - page not found',
  visual = defaultRepositoryErrorVisual,
  isEmbedded = false,
  className,
}: RepositoryErrorPageProps) {
  const RootElement = isEmbedded ? 'div' : 'main'

  return (
    <RootElement className={clsx(isEmbedded && 'm-3', className)}>
      <Blankslate border>
        <Blankslate.Visual>{visual}</Blankslate.Visual>
        <Blankslate.Heading>{heading}</Blankslate.Heading>
        <Blankslate.Description>{description}</Blankslate.Description>
        {action && <Blankslate.PrimaryAction href={action.url}>{action.label}</Blankslate.PrimaryAction>}
      </Blankslate>
    </RootElement>
  )
}
